// src/pages/ContentModerationDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Shield, MessageCircle, Package, ChevronRight } from 'lucide-react';
import ContentModerationPage from './ContentModerationPage';
import FlaggedGoGetsPage from './FlaggedGoGetsPage';
import FlaggedContentPage from './FlaggedContentPage';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import Button from '../components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { reportsService } from '../services/reportsService';

const ContentModerationDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('all');
  const [summaryCards, setSummaryCards] = useState([
    {
      title: 'All Reports',
      icon: Shield,
      pendingCount: 0,
      highPriorityCount: 0,
      color: 'blue',
      tabId: 'all',
    },
    {
      title: 'Flagged GoGets',
      icon: Package,
      pendingCount: 0,
      highPriorityCount: 0,
      color: 'orange',
      tabId: 'gogets',
    },
    {
      title: 'Flagged Messages',
      icon: MessageCircle,
      pendingCount: 0,
      highPriorityCount: 0,
      color: 'pink',
      tabId: 'messages',
    }
  ]);

  // Color definitions
  const colorMap = {
    blue: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      accent: 'bg-blue-600',
      hover: 'hover:bg-blue-700',
    },
    orange: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      accent: 'bg-orange-600',
      hover: 'hover:bg-orange-700',
    },
    pink: {
      bg: 'bg-pink-100',
      text: 'text-pink-800', 
      accent: 'bg-pink-600',
      hover: 'hover:bg-pink-700',
    },
    red: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      accent: 'bg-red-600',
      hover: 'hover:bg-red-700',
    }
  };

  // Parse active tab from URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/gogets')) {
      setActiveTab('gogets');
    } else if (path.includes('/messages')) {
      setActiveTab('messages');
    } else if (path.includes('/all')) {
      setActiveTab('all');
    } else {
      setActiveTab('overview');
    }
    
    console.log('[ContentModerationDashboard] Active tab set from URL:', {
      path,
      activeTab: path.includes('/gogets') ? 'gogets' : 
                 path.includes('/messages') ? 'messages' :
                 path.includes('/all') ? 'all' : 'overview'
    });
  }, [location]);
    
  // Update URL when tab changes
  const handleTabChange = (newTab) => {
    console.log('[ContentModerationDashboard] Tab changed to:', newTab);
    setActiveTab(newTab);
    if (newTab === 'overview') {
      navigate('/moderation');
    } else {
      navigate(`/moderation/${newTab}`);
    }
  };

  // Fetch statistics periodically
  useEffect(() => {
    fetchDashboardStats();
    
    // Set up polling every 30 seconds
    const intervalId = setInterval(fetchDashboardStats, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  const fetchDashboardStats = async () => {
    try {
      console.log('[ContentModerationDashboard] Fetching dashboard stats');
      const [allStats, gogetStats, messageStats] = await Promise.all([
        reportsService.getStatsByContentType('all'),
        reportsService.getStatsByContentType('goget'),
        reportsService.getStatsByContentType('message')
      ]);
      
      console.log('[ContentModerationDashboard] Stats received:', {
        all: allStats,
        gogets: gogetStats,
        messages: messageStats
      });
  
      // Update summary cards with emphasis on high priority items
      setSummaryCards([
        {
          title: 'All Reports',
          icon: Shield,
          pendingCount: allStats.pending || 0,
          highPriorityCount: allStats.highPriority || 0,
          color: allStats.highPriority > 0 ? 'red' : 'blue',
          tabId: 'all',
        },
        {
          title: 'Flagged GoGets',
          icon: Package,
          pendingCount: gogetStats.flagged || 0,
          highPriorityCount: gogetStats.highSeverity || 0,
          color: gogetStats.highSeverity > 0 ? 'red' : 'orange',
          tabId: 'gogets',
        },
        {
          title: 'Flagged Messages',
          icon: MessageCircle,
          pendingCount: messageStats.flagged || 0,
          highPriorityCount: messageStats.highSeverity || 0,
          color: messageStats.highSeverity > 0 ? 'red' : 'pink',
          tabId: 'messages',
        }
      ]);
    } catch (error) {
      console.error('[ContentModerationDashboard] Error fetching stats:', error);
    }
  };
  
  // Render high priority elements with visual emphasis
  const renderCard = (card) => {
    const colors = colorMap[card.color];
    const hasHighPriority = card.highPriorityCount > 0;
    
    return (
      <Card 
        key={card.tabId} 
        className={`relative overflow-hidden ${hasHighPriority ? 'ring-2 ring-red-500' : ''}`}
      >
        <div className={`absolute top-0 left-0 w-2 h-full ${colors.accent}`}></div>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${colors.bg}`}>
              <card.icon className={`h-6 w-6 ${colors.text}`} />
            </div>
            <CardTitle>{card.title}</CardTitle>
            {hasHighPriority && (
              <span className="ml-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-xs font-semibold text-red-800">
                !
              </span>
            )}
          </div>
          <CardDescription>
            Review moderation queue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Pending</span>
              <span className="text-lg font-bold">{card.pendingCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${hasHighPriority ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                High Priority
              </span>
              <span className={`text-lg font-bold ${hasHighPriority ? 'text-red-600' : ''}`}>
                {card.highPriorityCount}
              </span>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            variant={hasHighPriority ? "destructive" : "ghost"}
            className={`w-full flex items-center justify-between ${hasHighPriority ? 'text-white' : colors.text}`}
            onClick={() => handleTabChange(card.tabId)}
          >
            <span>{hasHighPriority ? 'Urgent Review Needed' : 'View Queue'}</span>
            <ChevronRight className="h-5 w-5" />
          </Button>
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Content Moderation</h1>
        <p className="mt-1 text-sm text-gray-500">
          Review and manage reported and flagged content
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="all">All Reports</TabsTrigger>
          <TabsTrigger value="gogets">Flagged GoGets</TabsTrigger>
          <TabsTrigger value="messages">Flagged Messages</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {summaryCards.map(renderCard)}
          </div>
        </TabsContent>
        
        <TabsContent value="all">
          <ContentModerationPage />
        </TabsContent>
        
        <TabsContent value="gogets">
          <FlaggedGoGetsPage />
        </TabsContent>
        
        <TabsContent value="messages">
          <FlaggedContentPage />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContentModerationDashboard;